import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  Paper,
  Divider,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Print as PrintIcon,
  Description,
  HistoryEdu,
  Person,
  DirectionsBoat,
  LocationOn,
  CalendarToday,
} from "@mui/icons-material";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useCompanyContext } from "../../Contexts/CompanyContext";
import { useSettings } from "../../Contexts/SettingsContext";
import { useTranslation } from "react-i18next";
import { useShipSettings } from "../../Contexts/ShipSettingsContext";
import dayjs from "dayjs";
import { renderNorV2 } from "../../print-v2";
import { getSelectedTrip } from "../../utils/voyageSelection";
import {
  getShipCallSign,
  getShipName,
  getVoyageNumber,
} from "../../utils/domainSelectors";

const NoticeOfReadiness = () => {
  const { t } = useTranslation("noticeofreadiness");
  const theme = useTheme();
  const { selectedTripId, selectedCargoIndex, currentVoyage } =
    useVoyageContextV2();

  const [loading, setLoading] = useState(true);
  const { companyLogo } = useCompanyContext();
  const { is24Hour, dateFormat } = useSettings();
  const { shipData } = useShipSettings();

  const callSign = getShipCallSign(shipData) || "";
  const date = dayjs().format(dateFormat || "YYYY-MM-DD");

  useEffect(() => {
    if (currentVoyage) setLoading(false);
  }, [currentVoyage]);

  const onPrint = (isRetendered) => {
    const trip = getSelectedTrip(currentVoyage, selectedTripId);

    if (!trip?.cargos?.[selectedCargoIndex]) {
      alert("Please select a cargo before printing.");
      return;
    }

    renderNorV2({
      currentVoyage,
      selectedTripId,
      selectedCargoIndex,
      companyLogo,
      retendered: isRetendered,
      is24Hour,
      t,
      callSign,
      date,
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 10,
          gap: 2,
        }}
      >
        <CircularProgress size={60} thickness={4.5} />
        <Typography variant="h6" color="textSecondary">
          Loading Voyage Data...
        </Typography>
      </Box>
    );
  }

  const trip = getSelectedTrip(currentVoyage, selectedTripId);

  const InfoRow = ({ icon, label, value }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}>
      <Box
        sx={{
          color: "primary.main",
          display: "flex",
          p: 1,
          borderRadius: 1.5,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
        }}
      >
        {React.cloneElement(icon, { fontSize: "small" })}
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            textTransform: "uppercase",
            fontWeight: 700,
            display: "block",
            mb: -0.5,
          }}
        >
          {label}
        </Typography>
        <Typography variant="body1" fontWeight="600">
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", pb: 5 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="900" gutterBottom>
          {t("title")}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Prepare and generate the official Notice of Readiness for signing.
        </Typography>
      </Box>

      {/* Document Preview Card */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          position: "relative",
        }}
      >
        <Box sx={{ position: "absolute", top: 24, right: 24, opacity: 0.5 }}>
          {companyLogo ? (
            <img
              src={companyLogo}
              alt="Logo"
              style={{ maxHeight: 50, filter: "grayscale(100%)" }}
            />
          ) : (
            <DirectionsBoat sx={{ fontSize: 40, color: "divider" }} />
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 4 }}>
          <HistoryEdu color="primary" />
          <Typography variant="h6" fontWeight="800">
            {t("voyageInfo.title")}
          </Typography>
        </Stack>

        <Grid container spacing={0}>
          <Grid sx={{ borderRight: { md: `1px solid ${theme.palette.divider}` }, pr: { md: 4 }, }} size={{ xs: 12, md: 6 }}>
            <Stack divider={<Divider flexItem sx={{ opacity: 0.5 }} />}>
              <InfoRow
                icon={<DirectionsBoat />}
                label={t("voyageInfo.vessel")}
                value={currentVoyage?.vessel || getShipName(shipData)}
              />
              <InfoRow
                icon={<Person />}
                label={t("voyageInfo.master")}
                value={trip?.crew?.masterName}
              />
              <InfoRow
                icon={<Description />}
                label={t("voyageInfo.voyage")}
                value={getVoyageNumber(currentVoyage)}
              />
            </Stack>
          </Grid>
          <Grid sx={{ pl: { md: 4 }, mt: { xs: 2, md: 0 } }} size={{ xs: 12, md: 6 }}>
            <Stack divider={<Divider flexItem sx={{ opacity: 0.5 }} />}>
              <InfoRow
                icon={<CalendarToday />}
                label={t("voyageInfo.date")}
                value={date}
              />
              <InfoRow
                icon={<LocationOn />}
                label={t("voyageInfo.port")}
                value={trip?.to}
              />
              <InfoRow
                icon={<Person />}
                label={t("voyageInfo.toDestination")}
                value={trip?.supplierReceiver}
              />
            </Stack>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 6,
            p: 2.5,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.info.main, 0.04),
            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
          }}
        >
          <Typography
            variant="body2"
            color="info.main"
            align="center"
            fontWeight="500"
          >
            The information above is retrieved from the voyage plan and will be
            used in the printed document.
          </Typography>
        </Box>
      </Paper>

      {/* Buttons */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 4 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={<PrintIcon />}
          onClick={() => onPrint(false)}
          sx={{ py: 1.8, borderRadius: 3, fontWeight: 800, fontSize: "1rem" }}
        >
          {t("voyageInfo.printFirst")}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          size="large"
          color="secondary"
          startIcon={<PrintIcon />}
          onClick={() => onPrint(true)}
          sx={{
            py: 1.8,
            borderRadius: 3,
            fontWeight: 800,
            fontSize: "1rem",
            borderWidth: 2,
            "&:hover": { borderWidth: 2 },
          }}
        >
          {t("voyageInfo.printRetendered")}
        </Button>
      </Stack>
    </Box>
  );
};

export default NoticeOfReadiness;
